import React, {
  Dispatch,
  useCallback,
  useContext,
  useLayoutEffect,
  useState
} from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { Button } from '../atoms/Button';
import { Panel } from '../atoms/Panel';
import { getContainerWidth } from '@cube3/common/utils/getContainerWidth';
import { isMobile } from 'react-device-detect';
import { SizeVariants } from '../../theme/themes';
import { AssetTileBar } from '../molecules/ReviewTools/AssetTileBar';

import { IconName } from '../atoms/Icon';
import { CommentBadge } from '../atoms/CommentBadge';
import { usePollReviewComments } from '../../../../main/src/components/app/layout/ReviewTool/Hooks/usePollReviewComments';
import { useReviewApprovals } from '../../../../main/src/components/app/layout/ReviewTool/Hooks/useReviewApprovals';
import { Asset } from '@cube3/common/model/schema/resources/asset';

export const useReviewViewTemplateCSS = makeCSS(({ theme }) => {
  return {
    root: {
      width: '100%',
      height: '100%'
    },
    header: {
      borderBottom: `1px solid ${theme.color.line['03']}`, // rgba(255, 255, 255, 0.04)
      width: '100%',
      height: 64,
      padding: '0 16px',
      boxSizing: 'border-box'
    },
    content: { display: 'flex', height: 'calc(100% - 64px)' },
    sidebarLeft: {
      transition: 'all 0.3s ease-in-out',
      background: theme.color.background.neutral,
      zIndex: 1
    },
    commentSidebar: {
      width: '100%',
      height: isMobile ? 'calc(100vh - 130px)' : '100vh',
      position: 'absolute',
      zIndex: 999,
      transition: 'width 0.3s ease-in-out',
      background: theme.color.background.neutral,
      top: isMobile ? 128 : ''
    },
    drawerHeader: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.color.line['03']}`, // rgba(255, 255, 255, 0.04)
      height: 64,
      padding: '0 16px',
      boxSizing: 'border-box'
    },
    main: {
      flexGrow: 1,
      position: 'relative'
    },
    withBorderRight: {
      borderRight: `1px solid ${theme.color.line['03']}`
    },
    mobileOverlay: {
      width: 'calc(100vw - 70px)',
      height: '100vh',
      position: 'absolute',
      zIndex: 999,
      background: theme.color.background.neutral,
      opacity: 0.5
    }
  };
});

interface Props {
  HeaderComponent: JSX.Element;
  NaviComponent?: JSX.Element;
  SideBarHeader: JSX.Element;
  MainComponent: JSX.Element;
  drawerWidth?: number;
  MobileLeftSideComponent?: JSX.Element;
  //settings button for mobile
  onClickSettings?: () => void;
  onMobileSidebarClick?: () => void;
  openSideBar?: boolean;
  setOpenSideBar?: Dispatch<boolean>;
  openCommentBar?: boolean;
}

export const ReviewViewTemplate = (props: Props) => {
  const {
    HeaderComponent,
    NaviComponent,
    MainComponent,
    drawerWidth = isMobile ? 360 : 400, //Note: adjust the animation part so we can use calc for the width
    SideBarHeader,
    MobileLeftSideComponent,
    onClickSettings,
    onMobileSidebarClick,
    openSideBar,
    setOpenSideBar,
    openCommentBar,
  } = props;



  const classes = useReviewViewTemplateCSS();




  return (
    <Panel
      backgroundColor={(theme) => theme.color.background.neutral}
      style={{ width: '100%', height: '100%' }}
      gradient={false}
    >
      <div className={classes.root}>
        <div className={classes.header}>{HeaderComponent}</div>
        <div className={classes.content}>
          <div
            className={`${classes.sidebarLeft} ${classes.withBorderRight}`}
            style={{
              width: openSideBar ? drawerWidth : NaviComponent ? 68 : 0,
              transition: 'width 0.3s ease-in-out'
            }}
          >
            {NaviComponent && <DrawerHeader
              open={openSideBar}
              setOpen={setOpenSideBar}
              width={drawerWidth}
              SideBarHeader={SideBarHeader}
              onClickSettings={onClickSettings}
            />}

            <div
              style={{
                boxSizing: 'border-box',
                marginLeft: openSideBar ? 0 : `-${drawerWidth + 1}px`,
                height: '100%',
                width: drawerWidth,
                transition: 'margin 0.3s ease-in-out',
                padding: '0 8px',
                paddingRight: '4px'
              }}
            >
              {NaviComponent}
            </div>
          </div>

          <div className={classes.main}>

            {onMobileSidebarClick && (
              <div
                className={classes.mobileOverlay}
                onClick={onMobileSidebarClick}
              ></div>
            )}
            {MainComponent}
          </div>
          {isMobile && openCommentBar && (
            <div className={`${classes.commentSidebar} `}>
              {MobileLeftSideComponent}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};

const DrawerHeader = (props) => {
  const { open, setOpen, width, SideBarHeader, onClickSettings } = props;
  let firstRender = true;

  const toggleSidebar = useCallback(() => {
    setOpen(isMobile ? false : (prev) => !prev);
  }, [setOpen]);

  const classes = useReviewViewTemplateCSS();

  const { containerWidth, setContainer } = getContainerWidth();
  const toRight = Math.max(0, width - 16 - containerWidth);

  useLayoutEffect(() => {
    return () => {
      firstRender = false;
    };
  }, []);

  return (
    <div className={classes.drawerHeader} data-prevent-deselect>
      <div
        style={{
          position: 'absolute',
          right: toRight, // 400 - 16(paddingLeft) - component width
          transition: firstRender ? null : 'right 0.3s ease-in-out'
        }}
      >
        <div ref={setContainer}>{SideBarHeader}</div>
      </div>

      <div style={{ position: 'absolute', right: 14, display: 'flex', gap: 8 }}>
        {isMobile && (
          <Button
            iconLeft="settings"
            label=""
            buttonStyle="ghost"
            size={isMobile ? SizeVariants.lg : SizeVariants.md}
            onClick={onClickSettings}
          />
        )}
        <Button
          data-set="preventDeselect"
          iconLeft={
            isMobile
              ? 'view_sidebar_left_fill'
              : open
                ? 'view_sidebar_left'
                : 'view_sidebar_left_fill'
          }
          onClick={toggleSidebar}
          background="secondary"
          buttonStyle="ghost"
          extraRootProps={{ 'data-prevent-deselect': true }}
          size={isMobile ? SizeVariants.lg : SizeVariants.md}
        />
      </div>
    </div>
  );
};
